/**
 * ハイドレートが完了せず、サーバが出した fallback の HTML だけが画面に残るクラスの保険。
 *
 * なぜインライン script なのか（ここが設計の核心）:
 * `app/loading.tsx` の従来のリロードは `useEffect` の中にある。effect は React が
 * commit したときにしか走らないため、**commit されない状況＝まさに保険が要る状況では
 * 起動しない**。2026-08-22 の実測（/catalog）では readyState が complete・
 * ネットワークも全 200 なのに、45 秒経っても effect が1つも走らず DOM は fallback のまま
 * だった。インライン script は**パース時点で実行される**ので、この状況でも動く。
 *
 * 安全設計（`app/loading.tsx` は全ルート共有なので、正常系を1ミリも変えないことが最優先）:
 * - commit されたら `HYDRATED_FLAG` が立ち、ウォッチドッグは発火時に**何もせず終わる**
 * - リロードは**1 回だけ**。2 回目は文言を出して止まる（無限リロードで自分を悪化させない）
 * - `sessionStorage` が使えなくても例外を投げない（プライベートモード等）
 *
 * 機構: `__tests__/loading-watchdog.test.ts`
 */

/** commit 済みを表すグローバルフラグ。`app/loading.tsx` の effect が立てる。 */
export const HYDRATED_FLAG = '__gforceHydrated'

// HYDRATED_FLAG は Window に無いキーなので、二段キャストの代わりに型拡張で宣言する。
// 🔴 型拡張は定数と同じこのモジュールに置く（2026-09-23）。以前は app/loading.tsx にだけあり、
//    components/layout/layout.tsx を読み込むテストを単独で型検査すると TS7015 になっていた。
//    interface の computed property name には unique symbol しか使えないため、リテラルで書く
//    （値は HYDRATED_FLAG と一致させること）。
declare global {
  interface Window {
    __gforceHydrated?: boolean
  }
}

/** ウォッチドッグの二重仕掛け防止フラグ。 */
export const WATCHDOG_FLAG = '__gforceLoadingWatchdog'

/** 発火までの待ち時間。従来の effect 版と同じ 20 秒（正常でも遅い画面で誤発火させない）。 */
export const LOADING_WATCHDOG_DELAY_MS = 20_000

/**
 * commit 済みの盾（`<Layout>` の LoadingShield）に対する見張りの待ち時間。
 *
 * インライン script の 20 秒より長いのは、キャッシュユーザーが無い初回認証が**正常でも**
 * 最悪これだけ直列に待つため（lib/providers/auth-provider.tsx・2026-09-13 Codex SO 指摘で実測）:
 *   getSession 13 秒 ＋ なりすまし確認 8 秒 ＋ /api/users 8 秒×3 回（＋待機 1.8 秒）＋ profile 8 秒 ≒ 55 秒。
 * これより短いと、遅いだけの正常な初回ログインをリロードで潰す。
 * 🔴 auth-provider 側の timeout を伸ばす時は、この値も合わせて伸ばす
 * （機構: `__tests__/loading-watchdog.test.ts` が下限を固定している）。
 */
export const LOADING_SHIELD_WATCHDOG_DELAY_MS = 60_000

/** 復帰不能を伝える要素の id。既定は hidden で、ウォッチドッグが最後の手段として表示する。 */
export const LOADING_STUCK_MESSAGE_ID = 'gforce-loading-stuck'

/** リロード回数を持つキー。タブを閉じれば消える（sessionStorage）。 */
export const LOADING_RETRY_STORAGE_KEY = 'gforce-loading-retry'

/**
 * `<script>{LOADING_WATCHDOG_SCRIPT}</script>` として埋め込むテキスト。
 *
 * React 19 は script の文字列 children を**エスケープせずそのまま出力する**ため、
 * ここに動的な値・利用者入力を混ぜてはならない（すべて静的リテラルであること）。
 * `</script` を含めるとその場で HTML が壊れるので、テストで固定している。
 *
 * 🔴 見えている間だけ数える（2026-10-08）。裏のタブ（document.visibilityState が 'hidden'）では
 *    ブラウザが描画もタイマーも止め・間引くため、正常な画面でも 20 秒のうちに表示が終わらず、
 *    裏で開いたタブが「読み込みに失敗しました」になっていた。hidden の間は数えず、
 *    見えたら 20 秒を最初から数え直す。
 *    MDN Page Visibility API https://developer.mozilla.org/en-US/docs/Web/API/Page_Visibility_API
 *    「"hidden": The page's content is not visible to the user, either due to the document's tab being in
 *     the background or part of a window that is minimized」／「Timers like setTimeout() are throttled in
 *     background/inactive tabs」
 */
export const LOADING_WATCHDOG_SCRIPT = `(function(){
if(window.${WATCHDOG_FLAG})return;
window.${WATCHDOG_FLAG}=1;
var t=0;
function fire(){
t=0;
document.removeEventListener('visibilitychange',onVis);
if(window.${HYDRATED_FLAG})return;
var n=0;
try{n=parseInt(window.sessionStorage.getItem('${LOADING_RETRY_STORAGE_KEY}')||'0',10)||0}catch(e){}
if(n<1){try{window.sessionStorage.setItem('${LOADING_RETRY_STORAGE_KEY}',String(n+1))}catch(e){}window.location.reload();return}
var el=document.getElementById('${LOADING_STUCK_MESSAGE_ID}');
if(el)el.hidden=false;
}
function arm(){if(!t)t=setTimeout(fire,${LOADING_WATCHDOG_DELAY_MS})}
function onVis(){if(document.visibilityState==='hidden'){if(t){clearTimeout(t);t=0}}else arm()}
document.addEventListener('visibilitychange',onVis);
if(document.visibilityState!=='hidden')arm();
})();`

/**
 * 「盾は描けている（commit 済み）のに、認証の解決待ちが終わらない」クラスの保険。
 *
 * インライン script は commit されない事故専用で、commit された後は何もしない。
 * 2026-09-13 実測: /login でセッション有効のとき画面内遷移で isLoading=true を遷移先へ
 * 持ち越し、Layout は commit できているのに LoadingShield が永久に残った
 * （再読み込みするまで「読み込み中」から動かない）。この形はインライン script では拾えない。
 *
 * 振る舞いはインライン script の発火時と同じにそろえる:
 * - リロードは**タブごとに 1 回だけ**（sessionStorage の回数を共有する）
 * - 2 回目は「読み込みに失敗しました」を出して止まる（無限リロードにしない）
 * - sessionStorage が使えなくても例外を投げない（回数を数えられない時はリロードせずメッセージ）
 *
 * 機構: `__tests__/loading-watchdog.test.ts` / `components/layout/__tests__/use-loading-shield-watchdog.test.tsx`
 */
export function recoverFromStuckLoading(): 'reloaded' | 'message-shown' {
  let n = 0
  try {
    n = parseInt(window.sessionStorage.getItem(LOADING_RETRY_STORAGE_KEY) || '0', 10) || 0
  } catch (_e) {
    // sessionStorage が使えない環境（プライベートモード等）は 0 回とみなす（インライン script と同じ）
    n = 0
  }
  if (n < 1) {
    let recorded = false
    try {
      window.sessionStorage.setItem(LOADING_RETRY_STORAGE_KEY, String(n + 1))
      recorded = window.sessionStorage.getItem(LOADING_RETRY_STORAGE_KEY) === String(n + 1)
    } catch (_e) {
      // 書けない＝回数を数えられない → 下でメッセージ表示へ倒す
      recorded = false
    }
    // 回数を数えられる時だけリロードする。数えられない環境でリロードすると
    // 20 秒ごとに無限リロードになるので、その場合はメッセージ表示へ倒す
    if (recorded) {
      window.location.reload()
      return 'reloaded'
    }
  }
  const el = document.getElementById(LOADING_STUCK_MESSAGE_ID)
  if (el) el.hidden = false
  return 'message-shown'
}

/**
 * 見えている間だけ数えるタイマー（インライン script と同じ数え方を React の見張りでも使う）。
 * hidden の間は数えず、見えたら delayMs を最初から数え直す。返す関数で止める。
 */
export function startVisibleTimeout(delayMs: number, onFire: () => void): () => void {
  let id: ReturnType<typeof setTimeout> | null = null
  const arm = () => {
    if (id === null)
      id = setTimeout(() => {
        id = null
        document.removeEventListener('visibilitychange', onVisibility)
        onFire()
      }, delayMs)
  }
  const disarm = () => {
    if (id !== null) clearTimeout(id)
    id = null
  }
  function onVisibility() {
    if (document.visibilityState === 'hidden') disarm()
    else arm()
  }
  document.addEventListener('visibilitychange', onVisibility)
  if (document.visibilityState !== 'hidden') arm()
  return () => {
    disarm()
    document.removeEventListener('visibilitychange', onVisibility)
  }
}

/**
 * 表示できたら、リロードの回数を 0 に戻す（キーを消す・読む側は無い＝0）。
 * 戻さないと、一度リロードした後のタブは次に詰まったとき、リロードせずにすぐ失敗の文言になる。
 * sessionStorage が使えない環境では何もせず false を返す（回数を数えられない環境はもともとリロードしない）。
 */
export function resetLoadingRetryCount(): boolean {
  try {
    window.sessionStorage.removeItem(LOADING_RETRY_STORAGE_KEY)
    return true
  } catch (_e) {
    // fail-open-ok: sessionStorage が使えない環境（プライベートモード等）は戻す回数も無い。画面は止めない
    return false
  }
}
